import { FieldValues } from 'react-hook-form';
import { Url } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { QueueAction, Schema, SchemaSection } from '@rossum/api-client/schemas';
import { SvgIcon, Tooltip, Typography } from '@rossum/ui/material';
import { FormulaPreviewTile } from '../../formulas/FormulaPreview/FormulaPreviewTile';
import { ActionTooltip } from '../actions/createHeader';
import { useActionOptions } from '../ThenSection/useActionOptions';
import { ValidRuleForm } from '../utils';

type TestSectionProps<T extends FieldValues & ValidRuleForm> = {
  schema: Schema;
  queueUrls: Url[];
  queue?: Queue;
  currentRuleConcept: T;
};

const ARTIFICIAL_CONDITION_ID = 'condition';

const createArtificialRuleSection = (formula: string): SchemaSection => ({
  id: 'rule_section',
  category: 'section',
  label: 'rule section',
  children: [
    {
      id: ARTIFICIAL_CONDITION_ID,
      category: 'datapoint',
      label: 'Formula condition',
      type: 'string',
      formula,
      uiConfiguration: { type: 'formula', edit: 'disabled' },
    },
  ],
});

const createRenderAction =
  (
    options: ReturnType<typeof useActionOptions>['actionOptions'],
    schema: Schema
  ) =>
  ({ action }: { action: QueueAction }) => {
    const config = options.find(knownAction =>
      knownAction.resolveAction(action)
    );
    const Icon = config?.icon;

    if (!config || !Icon) {
      return null;
    }

    return (
      <Tooltip
        title={
          <>
            <Typography variant="body2" fontWeight="bold">
              {config.name}
            </Typography>
            {config.header
              ? config.header({
                  value: action.payload,
                  schema,
                  RenderComponent: ActionTooltip,
                })
              : null}
          </>
        }
      >
        <SvgIcon>
          <Icon />
        </SvgIcon>
      </Tooltip>
    );
  };

export const TestSection = <T extends FieldValues & ValidRuleForm>({
  schema,
  queueUrls,
  queue,
  currentRuleConcept,
}: TestSectionProps<T>) => {
  const { actionOptions: options } = useActionOptions(
    currentRuleConcept['ruleActions']
  );

  return (
    <FormulaPreviewTile
      type="rule"
      currentFormulaId={ARTIFICIAL_CONDITION_ID}
      ActionComponent={createRenderAction(options, schema)}
      schemaContentDraft={[
        // Add artificial condition to the schema content
        createArtificialRuleSection(currentRuleConcept.triggerCondition),
        ...(schema.content ?? []),
      ]}
      schemaRulesDraft={[
        {
          ...currentRuleConcept,
          // Append current schema URL to make it work when adding new rule
          schema: schema.url,
          // Pretend that the rule is enabled for the sake of preview
          enabled: true,
        },
      ]}
      queueUrls={queueUrls}
      queue={queue}
    />
  );
};
